/** Окно «График строки P&L»: группа или статья сводки, текущий период против сравнения.
 *  Одна ось — ₽ по ПОКАЗАННОМУ числу (расход положительный, см. `calc.ts`) или, у расходов,
 *  % выручки своей корзины (выручка — `/trend` без среза); сравнение выровнено по позиции
 *  корзины (см. `itemTrend.ts`). Сверху — выбор среза: вся группа или любая её статья. */
import { X } from "lucide-react";
import React, { useId, useMemo, useRef, useState } from "react";
import {
  CartesianGrid,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import type { Grain, MartState, OzFilters } from "@/api/ozonAnalytics";
import { axisTick } from "@/lib/chart-theme";
import { useIsDark } from "@/lib/use-is-dark";
import { cn } from "@/lib/utils";
import { delta, deltaTone, grpKind, shareDelta, TONE_CLASS, type Kind } from "./calc";
import { plural } from "./cogs";
import { pnlCompactCells, PP_EPS } from "./compact";
import { isolatedDot } from "./isolatedDot";
import { compact, pct, pctSigned, pp, rub, rubSigned } from "./format";
import {
  bucketTip,
  defaultGrain,
  itemTrendPctSeries,
  itemTrendSeries,
  noPctReason,
  noRevenue,
  rangeLabel,
  unevenPair,
  type ItemTrendPctPoint,
  type ItemTrendPctSeries,
  type ItemTrendPoint,
  type ItemTrendSeries,
} from "./itemTrend";
import { useMp } from "./mp";
import { useTrendRevenue, useTrendSlice } from "./queries";
import { ErrorBox, Loading, Seg, Unreliable } from "./shared";
import { selectClass } from "./ui";
import { useDialogFocus } from "./useDialogFocus";

/** Статья группы из сводки: суммы — в знаке витрины. */
export interface ItemTrendRow {
  item: string;
  cur: number;
  cmp: number | null;
}

/** Группа P&L, по которой открыт график (иконка группы или её статьи): суммы — в знаке витрины,
 *  как в сводке. */
export interface ItemTrendTarget {
  grp: string;
  grpLabel: string;
  /** статья, по которой открыли окно; null — вся группа */
  item: string | null;
  /** итоги группы */
  cur: number;
  cmp: number | null;
  /** статьи группы — срезы, между которыми переключается окно */
  items: ItemTrendRow[];
  /** выручка сводки — база колонки «% выр.»; итог окна в % сверяется с ней */
  revCur: number;
  revCmp: number | null;
  /** сборка витрины, по которой посчитана сводка (envelope `mart`) — сверка итогов окна честна,
   *  только если срез посчитан по той же сборке */
  mart: MartState;
}

/** Единицы оси: рубли или % выручки своей корзины (только у расходов). */
export type TrendUnit = "rub" | "pct";

const UNITS: { id: TrendUnit; label: string }[] = [
  { id: "rub", label: "₽" },
  { id: "pct", label: "% выр." },
];

const NO_REV = "нет выручки — % не определён";
const NEG_REV = "выручка отрицательная (возвраты больше продаж) — % не определён";

const GRAINS: { id: Grain; label: string }[] = [
  { id: "day", label: "Дни" },
  { id: "week", label: "Недели" },
  { id: "month", label: "Месяцы" },
];

/** Расхождение итога окна со строкой таблицы больше полтинника — баг, показываем явно. */
const MISMATCH_EPS = 0.5;

/** Одна и та же сборка витрины: тот же сигнал, что у кэша бэка (`signal_of`: built_at + диапазон). */
const sameMart = (a: MartState, b: MartState): boolean =>
  a.built_at === b.built_at && a.date_from === b.date_from && a.date_to === b.date_to;

/** Ряд окна и то, по одной ли сборке витрины посчитаны срез и сводка; error — нарушен контракт. */
type Built = { series: ItemTrendSeries; sameBuild: boolean } | { error: Error } | null;
type BuiltPct = { series: ItemTrendPctSeries; sameBuild: boolean } | { error: Error } | null;

export function ItemTrendDialog({
  filters,
  target,
  warning = null,
  onClose,
}: {
  filters: OzFilters;
  target: ItemTrendTarget;
  /** Δ ненадёжна (период сравнения вне данных витрины) — тот же значок, что у Δ в таблице */
  warning?: string | null;
  onClose: () => void;
}): React.ReactElement {
  const titleId = useId();
  const dialogRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const [grain, setGrain] = useState<Grain>(() =>
    defaultGrain({ from: filters.date_from, to: filters.date_to }),
  );
  const [item, setItem] = useState<string | null>(target.item);
  // ₽/% — состояние окна, как и шаг: между открытиями не запоминается
  const [unit, setUnit] = useState<TrendUnit>("rub");
  const mp = useMp();
  const kind = grpKind(target.grp);
  // у выручки «% выручки» — всегда 100 %: переключателя нет
  const canPct = kind === "expense";
  const pctMode = canPct && unit === "pct";
  const q = useTrendSlice(mp.id, filters, grain, target.grp, item);
  const revQ = useTrendRevenue(mp.id, filters, grain, pctMode);
  // строка сводки выбранного среза — для сверки итога окна
  const row = useMemo(
    (): ItemTrendRow | undefined =>
      item === null
        ? { item: target.grpLabel, cur: target.cur, cmp: target.cmp }
        : target.items.find((r) => r.item === item),
    [item, target],
  );

  // фокус на крестик и обратно, Esc, ловушка Tab — общий хук окон страницы
  useDialogFocus(dialogRef, closeRef, onClose);

  // контракт бэка (ось, эхо среза) нарушен — это ошибка, а не пустой график
  const built = useMemo((): Built => {
    if (!q.data) return null;
    try {
      if (!row) throw new Error(`статьи «${item}» нет в группе «${target.grpLabel}» сводки`);
      if (q.data.grp !== target.grp || q.data.item !== item) {
        throw new Error(
          `тренд: ответ на другой срез (${q.data.grp} / ${q.data.item ?? "вся группа"})`,
        );
      }
      return {
        series: itemTrendSeries(
          q.data,
          target.grp,
          kind,
          { from: filters.date_from, to: filters.date_to },
          filters.cmp_from && filters.cmp_to ? { from: filters.cmp_from, to: filters.cmp_to } : null,
        ),
        sameBuild: sameMart(q.data.mart, target.mart),
      };
    } catch (e) {
      return { error: e as Error };
    }
  }, [q.data, row, item, target.grp, target.grpLabel, target.mart, kind, filters]);

  // % выручки: та же ось корзин, выручка — из `/trend` без среза
  const builtPct = useMemo((): BuiltPct => {
    if (!pctMode || !built || !("series" in built) || !revQ.data) return null;
    try {
      return {
        series: itemTrendPctSeries(built.series, revQ.data, kind),
        sameBuild: sameMart(revQ.data.mart, target.mart),
      };
    } catch (e) {
      return { error: e as Error };
    }
  }, [pctMode, built, revQ.data, kind, target.mart]);

  const hasCmp = !!(filters.cmp_from && filters.cmp_to);
  const title = item ? `${target.grpLabel} › ${item}` : target.grpLabel;
  const n = target.items.length;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4 dark:bg-black/60"
      onClick={onClose}
      data-testid="item-trend-backdrop"
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="max-h-[90vh] w-full max-w-4xl overflow-auto rounded-lg border bg-background p-4 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-3 flex items-start gap-3">
          <div className="min-w-0">
            <h3 id={titleId} className="text-base font-semibold">
              {title}
            </h3>
            {n > 1 && (
              <select
                aria-label="Срез графика"
                className={cn(selectClass, "my-1 max-w-full")}
                value={item === null ? "" : String(target.items.findIndex((r) => r.item === item))}
                onChange={(e) =>
                  setItem(e.target.value === "" ? null : target.items[Number(e.target.value)]!.item)
                }
              >
                <option value="">
                  Вся группа · {n} {plural(n, "статья", "статьи", "статей")}
                </option>
                {target.items.map((r, i) => (
                  <option key={r.item} value={String(i)}>
                    {r.item}
                  </option>
                ))}
              </select>
            )}
            <div className="text-xs text-muted-foreground" data-testid="item-trend-periods">
              {rangeLabel({ from: filters.date_from, to: filters.date_to })}
              {hasCmp &&
                ` против ${rangeLabel({ from: filters.cmp_from!, to: filters.cmp_to! })}`}
            </div>
          </div>
          <div className="ml-auto flex items-center gap-2">
            <Seg label="Шаг графика" value={grain} options={GRAINS} onChange={setGrain} />
            {canPct && (
              <Seg label="Единицы графика" value={unit} options={UNITS} onChange={setUnit} />
            )}
            <button
              type="button"
              onClick={onClose}
              className="rounded p-1 hover:bg-accent"
              aria-label="Закрыть"
              ref={closeRef}
            >
              <X className="size-4" />
            </button>
          </div>
        </div>

        {q.isLoading && <Loading text="Строю график…" />}
        {q.isError && <ErrorBox error={q.error} what="график" />}
        {built && "error" in built && <ErrorBox error={built.error} what="график" />}
        {built && "series" in built && row && !pctMode && (
          <ItemTrendBody
            series={built.series}
            pctSeries={null}
            row={row}
            target={target}
            kind={kind}
            grain={grain}
            warning={warning}
            sameBuild={built.sameBuild}
          />
        )}
        {built && "series" in built && row && pctMode && (
          <>
            {revQ.isPending && <Loading text="Считаю выручку корзин…" />}
            {revQ.isError && <ErrorBox error={revQ.error} what="выручку корзин для % выручки" />}
            {builtPct && "error" in builtPct && (
              <ErrorBox error={builtPct.error} what="график в % выручки" />
            )}
            {builtPct && "series" in builtPct && (
              <ItemTrendBody
                series={built.series}
                pctSeries={builtPct.series}
                row={row}
                target={target}
                kind={kind}
                grain={grain}
                warning={warning}
                sameBuild={built.sameBuild && builtPct.sameBuild}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
}

function ItemTrendBody({
  series,
  pctSeries,
  row,
  target,
  kind,
  grain,
  warning,
  sameBuild,
}: {
  series: ItemTrendSeries;
  /** ряд в % выручки; null — режим ₽ */
  pctSeries: ItemTrendPctSeries | null;
  /** строка сводки выбранного среза */
  row: ItemTrendRow;
  target: ItemTrendTarget;
  kind: Kind;
  grain: Grain;
  warning: string | null;
  /** срез (и выручка в режиме %) и сводка посчитаны по одной сборке витрины */
  sameBuild: boolean;
}): React.ReactElement {
  const isDark = useIsDark();
  const curColor = isDark ? "#3987e5" : "#2a78d6";
  const cmpColor = isDark ? "#78716c" : "#a8a29e";
  const hasCmp = series.totalCmp !== null;
  const points: ItemTrendPoint[] = pctSeries ? pctSeries.points : series.points;

  // итог окна = строка таблицы; расхождение по ОДНОЙ сборке витрины — баг (срез бэка ≠ сводка),
  // видно сразу. Между загрузкой сводки и среза витрину могли пересобрать — тогда итоги честно
  // разные: не красная тревога, а просьба обновить страницу (числа не подменяем)
  const mismatch: string[] = [];
  if (Math.abs(series.totalCur - row.cur) > MISMATCH_EPS) mismatch.push("текущий период");
  if (
    series.totalCmp !== null &&
    row.cmp !== null &&
    Math.abs(series.totalCmp - row.cmp) > MISMATCH_EPS
  ) {
    mismatch.push("период сравнения");
  }
  // % выручки: Σ выручки окна = выручка сводки (база колонки «% выр.»)
  if (pctSeries) {
    if (Math.abs(pctSeries.revCur - target.revCur) > MISMATCH_EPS) {
      mismatch.push("выручка текущего периода");
    }
    if (
      pctSeries.revCmp !== null &&
      target.revCmp !== null &&
      Math.abs(pctSeries.revCmp - target.revCmp) > MISMATCH_EPS
    ) {
      mismatch.push("выручка периода сравнения");
    }
  }

  // [Сейчас, Было, Δ п.п., Δ]; в режиме ₽ доли выручки в окне нет — Δ п.п. не показываем
  const [now, was, , d] = pnlCompactCells(series.totalCur, series.totalCmp, 0, null, hasCmp, kind);

  return (
    <div className="space-y-3">
      {mismatch.length > 0 && !sameBuild && (
        <div
          className="rounded-md border bg-muted/40 px-3 py-2 text-xs text-muted-foreground"
          data-testid="item-trend-rebuilt"
        >
          Витрина пересобрана между загрузкой сводки и графика — обновите страницу: сумма графика
          и строка сводки посчитаны по разным сборкам ({mismatch.join(", ")}).
        </div>
      )}
      {mismatch.length > 0 && sameBuild && (
        <div
          role="alert"
          className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700 dark:border-red-900 dark:bg-red-950 dark:text-red-400"
        >
          Сумма графика не совпадает со строкой сводки ({mismatch.join(", ")}) — это ошибка
          расчёта, сообщите разработчику.
        </div>
      )}
      {pctSeries ? (
        <PctTotals s={pctSeries} hasCmp={hasCmp} kind={kind} warning={warning} />
      ) : (
        <div className="flex flex-wrap gap-x-6 gap-y-2" data-testid="item-trend-totals">
          <Stat label="Сейчас" main={now!.main} title={now!.title} />
          {hasCmp && was && d && (
            <>
              <Stat label="Было" main={was.main} title={was.title} muted />
              <div>
                <div className="text-xs text-muted-foreground">
                  Δ ₽ · Δ %
                  <Unreliable warning={warning} />
                </div>
                <div
                  className={cn(
                    "text-lg font-semibold tabular-nums",
                    d.mainTone && d.mainTone !== "negative"
                      ? TONE_CLASS[d.mainTone]
                      : "",
                  )}
                >
                  {d.main}
                  {d.sub[0] && ` · ${d.sub[0].text}`}
                </div>
              </div>
            </>
          )}
        </div>
      )}

      <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-foreground">
        <span className="inline-flex items-center gap-1.5">
          <span className="inline-block h-0.5 w-5" style={{ background: curColor }} />
          Сейчас
        </span>
        {hasCmp && (
          <span className="inline-flex items-center gap-1.5">
            <span
              className="inline-block w-5 border-t-2 border-dashed"
              style={{ borderColor: cmpColor }}
            />
            Было
          </span>
        )}
      </div>

      <ResponsiveContainer width="100%" height={300}>
        <LineChart data={points} margin={{ top: 8, right: 12, bottom: 0, left: 0 }}>
          <CartesianGrid vertical={false} strokeOpacity={0.25} />
          <XAxis
            dataKey="i"
            type="category"
            tick={axisTick(isDark)}
            tickLine={false}
            axisLine={false}
            tickFormatter={(i: number) => points[i]?.label ?? ""}
            minTickGap={12}
          />
          <YAxis
            tick={axisTick(isDark)}
            tickLine={false}
            axisLine={false}
            width={64}
            tickFormatter={(v: number) => (pctSeries ? pct(v) : compact(v))}
          />
          <ReferenceLine y={0} strokeOpacity={0.5} />
          <Tooltip
            cursor={{
              stroke: isDark ? "#a8a29e" : "#78716c",
              strokeDasharray: "3 3",
            }}
            content={
              <ItemTip grain={grain} kind={kind} hasCmp={hasCmp} unit={pctSeries ? "pct" : "rub"} />
            }
          />
          {hasCmp && (
            <Line
              dataKey="cmp"
              name="Было"
              stroke={cmpColor}
              strokeWidth={1.5}
              strokeDasharray="5 4"
              dot={pctSeries ? isolatedDot(points, "cmp", cmpColor) : false}
              activeDot={{ r: 3 }}
              isAnimationActive={false}
            />
          )}
          <Line
            dataKey="cur"
            name="Сейчас"
            stroke={curColor}
            strokeWidth={2}
            dot={
              points.length <= 31
                ? { r: 2.5 }
                : pctSeries
                  ? isolatedDot(points, "cur", curColor)
                  : false
            }
            activeDot={{ r: 4 }}
            isAnimationActive={false}
          />
        </LineChart>
      </ResponsiveContainer>
      {pctSeries &&
        (pctSeries.noRevCur > 0 ||
          pctSeries.noRevCmp > 0 ||
          pctSeries.negRevCur > 0 ||
          pctSeries.negRevCmp > 0) && (
          <p className="text-xs text-amber-700 dark:text-amber-400" data-testid="item-trend-no-rev">
            Корзины, где % не определён (на графике разрыв):{" "}
            {[
              [pctSeries.noRevCur, pctSeries.noRevCmp, "без выручки"],
              [pctSeries.negRevCur, pctSeries.negRevCmp, "с отрицательной выручкой"],
            ]
              .filter(([c, m]) => (c as number) > 0 || (m as number) > 0)
              .map(([c, m, label]) =>
                `${label}: ${[
                  (c as number) > 0 && `«Сейчас» — ${c}`,
                  (m as number) > 0 && `«Было» — ${m}`,
                ]
                  .filter(Boolean)
                  .join(", ")}`,
              )
              .join("; ")}
            .
          </p>
        )}
      <p className="text-[11px] text-muted-foreground">
        {pctSeries &&
          "% — доля выручки своей корзины; итог — сумма статьи за период ÷ выручка периода, как в колонке «% выр.» сводки. "}
        {kind === "expense"
          ? "Расходы показаны без минуса; рост расходов — красный. "
          : ""}
        {hasCmp &&
          "Сравнение — по порядку корзин: 1-я корзина периода против 1-й корзины сравнения."}
        {grain !== "day" &&
          " Крайние корзины могут быть неполными — обрезаны границами периода; у пары корзин разной длины Δ в подсказке не считается."}
      </p>
    </div>
  );
}

/** Шапка окна в % выручки: итог — Σ статьи ÷ Σ выручки периода, Δ — в п.п. (как в таблице). */
function PctTotals({
  s,
  hasCmp,
  kind,
  warning,
}: {
  s: ItemTrendPctSeries;
  hasCmp: boolean;
  kind: Kind;
  warning: string | null;
}): React.ReactElement {
  const diff = shareDelta(s.shareCur, s.shareCmp);
  return (
    <div className="flex flex-wrap gap-x-6 gap-y-2" data-testid="item-trend-totals">
      <Stat label="Сейчас" main={pct(s.shareCur)} title={s.shareCur === null ? NO_REV : null} />
      {hasCmp && (
        <>
          <Stat
            label="Было"
            main={pct(s.shareCmp)}
            title={s.shareCmp === null ? NO_REV : null}
            muted
          />
          <div>
            <div className="text-xs text-muted-foreground">
              Δ п.п.
              <Unreliable warning={warning} />
            </div>
            <div
              className={cn(
                "text-lg font-semibold tabular-nums",
                diff !== null && TONE_CLASS[deltaTone(diff, PP_EPS, kind)],
              )}
            >
              {pp(diff)}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function Stat({
  label,
  main,
  title,
  muted = false,
}: {
  label: string;
  main: string;
  title: string | null;
  muted?: boolean;
}): React.ReactElement {
  return (
    <div>
      <div className="text-xs text-muted-foreground">{label}</div>
      <div
        className={cn("text-lg font-semibold tabular-nums", muted && "text-muted-foreground")}
        title={title ?? undefined}
      >
        {main}
      </div>
    </div>
  );
}

export function ItemTip({
  active,
  payload,
  grain,
  kind,
  hasCmp,
  unit = "rub",
}: {
  active?: boolean;
  payload?: { payload: ItemTrendPoint | ItemTrendPctPoint }[];
  grain: Grain;
  kind: Kind;
  hasCmp: boolean;
  /** pct — точки `ItemTrendPctPoint`: доля выручки, под ней рубли статьи и выручка корзины */
  unit?: TrendUnit;
}): React.ReactElement | null {
  const p = active ? payload?.[0]?.payload : undefined;
  if (!p) return null;
  if (unit === "pct")
    return <PctTip p={p as ItemTrendPctPoint} grain={grain} kind={kind} hasCmp={hasCmp} />;
  // неполные крайние корзины разной длины (неделя/месяц) — Δ не сравнима, вместо неё пометка
  const uneven = unevenPair(p);
  const d = p.cur !== null && p.cmp !== null && !uneven ? delta(p.cur, p.cmp) : null;
  const tone = d ? deltaTone(d.abs, 0.5, kind) : "neutral";
  return (
    <div
      className="rounded-md border bg-background px-3 py-2 text-xs text-foreground shadow-md"
      data-testid="item-trend-tip"
    >
      {p.curBucket !== null && (
        <div className="flex justify-between gap-4 tabular-nums">
          <span>Сейчас · {bucketTip(p.curBucket, grain)}</span>
          <span className="font-medium">{p.cur === null ? "—" : rub(p.cur)}</span>
        </div>
      )}
      {hasCmp && p.cmpBucket !== null && (
        <div className="flex justify-between gap-4 tabular-nums text-muted-foreground">
          <span>Было · {bucketTip(p.cmpBucket, grain)}</span>
          <span>{p.cmp === null ? "—" : rub(p.cmp)}</span>
        </div>
      )}
      {d && (
        <div className="mt-1 flex justify-between gap-4 border-t pt-1 tabular-nums">
          <span>Δ</span>
          <span className={TONE_CLASS[tone]}>
            {rubSigned(d.abs)} · {pctSigned(d.rel)}
          </span>
        </div>
      )}
      {uneven && (
        <div className="mt-1 border-t pt-1 text-muted-foreground" data-testid="item-trend-uneven">
          {uneven}
        </div>
      )}
    </div>
  );
}

/** Подсказка точки в % выручки: доля, под ней рубли статьи и выручка корзины; корзина без
 *  выручки — явная пометка вместо 0 %. Δ — в п.п. (и у пары разной длины); для неё — справочная пометка. */
function PctTip({
  p,
  grain,
  kind,
  hasCmp,
}: {
  p: ItemTrendPctPoint;
  grain: Grain;
  kind: Kind;
  hasCmp: boolean;
}): React.ReactElement {
  // доля от выручки СВОЕЙ корзины от её длины не зависит — Δ п.п. считается и у неравной пары
  const uneven = unevenPair(p);
  const diff = p.cur !== null && p.cmp !== null ? p.cur - p.cmp : null;
  const side = (
    label: string,
    bucket: string,
    value: number | null,
    rubV: number | null,
    rev: number | null,
    muted: boolean,
  ): React.ReactElement => (
    <div className={cn("tabular-nums", muted && "text-muted-foreground")}>
      <div className="flex justify-between gap-4">
        <span>
          {label} · {bucketTip(bucket, grain)}
        </span>
        <span className={cn(!muted && "font-medium")}>{value === null ? "—" : pct(value)}</span>
      </div>
      <div className="text-[11px] text-muted-foreground">
        {rubV === null ? "—" : rub(rubV)}
        {" · "}
        {noRevenue(bucket, value) && noPctReason(rev) === "negative" ? (
          <span data-testid="item-trend-tip-neg-rev">
            {rev === null ? "—" : rub(rev)} · {NEG_REV}
          </span>
        ) : noRevenue(bucket, value) ? (
          <span data-testid="item-trend-tip-no-rev">{NO_REV}</span>
        ) : (
          `выручка ${rev === null ? "—" : rub(rev)}`
        )}
      </div>
    </div>
  );
  return (
    <div
      className="space-y-1 rounded-md border bg-background px-3 py-2 text-xs text-foreground shadow-md"
      data-testid="item-trend-tip"
    >
      {p.curBucket !== null && side("Сейчас", p.curBucket, p.cur, p.curRub, p.curRev, false)}
      {hasCmp && p.cmpBucket !== null && side("Было", p.cmpBucket, p.cmp, p.cmpRub, p.cmpRev, true)}
      {diff !== null && (
        <div className="flex justify-between gap-4 border-t pt-1 tabular-nums">
          <span>Δ</span>
          <span className={TONE_CLASS[deltaTone(diff, PP_EPS, kind)]}>{pp(diff)}</span>
        </div>
      )}
      {uneven && (
        <div className="border-t pt-1 text-muted-foreground" data-testid="item-trend-uneven">
          {uneven}
        </div>
      )}
    </div>
  );
}
